/**
 * Authentication Manager
 * Singleton that manages the active authentication provider
 * Provides a unified interface for all authentication operations
 */

import type { AuthProvider, UserContext, EmailCredentials } from './types';
import { EmailAuthProvider } from './providers/EmailAuthProvider';
import { OidcAuthProvider } from './providers/OidcAuthProvider';
import type { AppConfig } from '../app-config';

export interface ExternalAuthAdapter {
  isAuthenticated: boolean;
  user?: UserContext | null;
  signIn?: (credentials?: EmailCredentials) => Promise<void> | void;
  signOut?: () => Promise<void> | void;
  refreshUser?: () => Promise<void> | void;
  getAccessToken?: () => Promise<string | null> | string | null;
  refreshAccessToken?: () => Promise<string | null> | string | null;
}

class ExternalAuthProviderAdapter implements AuthProvider {
  readonly name = 'external' as const;

  constructor(private readonly getConfig: () => ExternalAuthAdapter) {}

  async isAuthenticated(): Promise<boolean> {
    return Boolean(this.getConfig().isAuthenticated);
  }

  async getCurrentUser(): Promise<UserContext | null> {
    return this.getConfig().user || null;
  }

  async signIn(credentials?: EmailCredentials): Promise<void> {
    await this.getConfig().signIn?.(credentials);
  }

  async signOut(): Promise<void> {
    await this.getConfig().signOut?.();
  }

  async refreshTokens(): Promise<void> {
    await this.getConfig().refreshAccessToken?.();
  }

  async getAccessToken(): Promise<string | null> {
    return await this.getConfig().getAccessToken?.() || null;
  }
}

export class AuthManager {
  private static instance: AuthManager | null = null;
  private provider: AuthProvider | null = null;
  private initialized = false;
  private initializationPromise: Promise<void> | null = null;
  private externalAuth: ExternalAuthAdapter | null = null;

  private constructor() {}

  /**
   * Get singleton instance
   */
  static getInstance(): AuthManager {
    if (!AuthManager.instance) {
      AuthManager.instance = new AuthManager();
    }
    return AuthManager.instance;
  }

  /**
   * Use host-application authentication instead of the built-in OIDC/email flows.
   */
  useExternalAuth(config: ExternalAuthAdapter): void {
    this.externalAuth = config;
    this.provider = new ExternalAuthProviderAdapter(() => this.externalAuth || config);
    this.initialized = true;
    this.initializationPromise = null;
  }

  /**
   * Initialize with app configuration
   * Must be called before using any auth methods
   */
  async initialize(config: AppConfig): Promise<void> {
    if (this.initialized) {
      console.warn('AuthManager already initialized');
      return;
    }

    // If initialization is in progress, wait for it
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    // Start initialization
    this.initializationPromise = (async () => {
      // Select provider based on config
      if (config.oidcEnabled) {
        this.provider = new OidcAuthProvider();
        console.log('AuthManager: Initialized with OIDC provider');
      } else {
        this.provider = new EmailAuthProvider();
        console.log('AuthManager: Initialized with Email provider');
      }

      this.initialized = true;
    })();

    return this.initializationPromise;
  }

  /**
   * Wait for initialization to complete
   * If not initialized yet, polls until initialization starts
   */
  private async waitForInitialization(): Promise<void> {
    // If already initialized, return immediately
    if (this.initialized) {
      return;
    }

    // If initialization is in progress, wait for it
    if (this.initializationPromise) {
      await this.initializationPromise;
      return;
    }

    // If initialize() hasn't been called yet, poll until it starts
    // This handles the race condition where methods are called before initialize()
    const maxWaitTime = 10000; // 10 seconds max wait
    const pollInterval = 50; // Check every 50ms
    const startTime = Date.now();

    while (!this.initialized && !this.initializationPromise) {
      if (Date.now() - startTime > maxWaitTime) {
        throw new Error('AuthManager initialization timeout. Make sure AuthProvider is mounted.');
      }
      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }

    // Now initialization has started, wait for it to complete
    if (this.initializationPromise) {
      await this.initializationPromise;
    }
  }

  /**
   * Get the active provider
   * Throws if not initialized
   */
  private getProvider(): AuthProvider {
    if (!this.provider) {
      throw new Error('AuthManager not initialized. Call initialize() first.');
    }
    return this.provider;
  }

  /**
   * Get the active provider (asynchronous)
   * Waits for initialization if needed
   * Throws if not initialized
   */
  private async getProviderAsync(): Promise<AuthProvider> {
    await this.waitForInitialization();
    return this.getProvider();
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      const provider = await this.getProviderAsync();
      return await provider.isAuthenticated();
    } catch (error) {
      console.error('Failed to check authentication:', error);
      return false;
    }
  }

  /**
   * Get current user context
   */
  async getCurrentUser(): Promise<UserContext | null> {
    try {
      const provider = await this.getProviderAsync();
      return await provider.getCurrentUser();
    } catch (error) {
      console.error('Failed to get current user:', error);
      return null;
    }
  }

  /**
   * Sign in with credentials
   * For email: pass { email, password }
   * For OIDC: pass nothing (will redirect)
   */
  async signIn(credentials?: EmailCredentials): Promise<void> {
    const provider = await this.getProviderAsync();
    return await provider.signIn(credentials);
  }

  /**
   * Sign out
   */
  async signOut(): Promise<void> {
    const provider = await this.getProviderAsync();
    return await provider.signOut();
  }

  /**
   * Refresh authentication tokens
   */
  async refreshTokens(): Promise<void> {
    const provider = await this.getProviderAsync();
    if (provider.refreshTokens) {
      return await provider.refreshTokens();
    }
  }

  /**
   * Get access token for API calls
   */
  async getAccessToken(): Promise<string | null> {
    try {
      const provider = await this.getProviderAsync();
      return await provider.getAccessToken();
    } catch (error) {
      console.error('Failed to get access token:', error);
      return null;
    }
  }

  /**
   * Get current authentication mode
   */
  getAuthMode(): 'email' | 'oidc' | 'external' | 'none' {
    if (!this.provider) {
      return 'none';
    }
    return this.provider.name;
  }

  /**
   * Check if initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }
}

/**
 * Convenience function to get AuthManager instance
 */
export const getAuthManager = () => AuthManager.getInstance();
